import React, { useState, useEffect } from 'react';
import { FaEye, FaMapMarkerAlt, FaBriefcase, FaUserCheck, FaClock, FaGraduationCap, FaVenusMars, FaPrayingHands, FaUser } from 'react-icons/fa';
import { motion } from 'framer-motion';
import api from '../../services/api';

const ViewedProfiles = ({ onViewProfile }) => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Helper function - SAME as in Matches, Home, Shortlisted
    const getProfileImage = (photoPath, gender) => {
        if (!photoPath) {
            return gender === 'Female'
                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                : 'https://randomuser.me/api/portraits/men/85.jpg';
        }

        if (photoPath.startsWith('http') || photoPath.startsWith('data:') || photoPath.startsWith('blob:')) {
            return photoPath;
        }
        const BASE_URL = api.defaults.baseURL;
        const baseUrl = BASE_URL.replace("/api", "");
        const cleanPath = photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
        return `${baseUrl}${cleanPath}`;
    };

    useEffect(() => {
        fetchViewedProfiles();
    }, []);

    const fetchViewedProfiles = async () => {
        try {
            const response = await api.get('/viewed-profiles');
            console.log('Viewed profiles response:', response.data);

            // Deduplicate profiles by ID - keep only the most recent view
            const uniqueProfiles = {};
            (response.data || []).forEach(profile => {
                if (!uniqueProfiles[profile._id] || new Date(profile.viewedAt) > new Date(uniqueProfiles[profile._id].viewedAt)) {
                    uniqueProfiles[profile._id] = profile;
                }
            });

            // Convert to array and sort by newest first
            const deduplicated = Object.values(uniqueProfiles)
                .sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt));

            setProfiles(deduplicated);
            setError(null);
        } catch (error) {
            console.error('Error fetching viewed profiles:', error);
            setError(error.response?.data?.message || 'Failed to load viewed profiles');
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    };

    const formatTimeAgo = (date) => {
        if (!date) return 'Recently';

        const now = new Date();
        const viewedDate = new Date(date);
        const diffMs = now - viewedDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return viewedDate.toLocaleDateString();
    };

    const calculateAge = (dob) => {
        if (!dob) return 'N/A';
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const handleViewProfile = (profile) => {
    if (onViewProfile) {
        onViewProfile(profile._id);
    }
};


    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading viewed profiles...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-[1400px] mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <p className="text-red-600 font-medium">{error}</p>
                    <button
                        onClick={fetchViewedProfiles}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-4 py-8">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Recently Viewed Profiles</h1>
                        <p className="text-gray-600">
                            Profiles you've recently looked at
                            <span className="font-semibold text-rose-600 ml-1">{profiles.length} profiles</span>
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-rose-600">{profiles.length}</div>
                            <div className="text-sm text-gray-500">Viewed</div>
                        </div>
                        {/* <div className="h-8 w-px bg-gray-200"></div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">
                                {profiles.length > 0 ?
                                    Math.round(profiles.reduce((sum, p) => sum + calculateAge(p.basicInfo?.dob || 0), 0) / profiles.length)
                                    : 0}
                            </div>
                            <div className="text-sm text-gray-500">Avg Age</div>
                        </div> */}
                    </div>
                </div>
            </div>

            {profiles.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <div className="text-5xl mb-4 text-gray-300">👀</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No viewed profiles yet</h3>
                    <p className="text-gray-500 max-w-md mx-auto mb-6">
                        Start exploring profiles to see them here!
                    </p>
                    <button
                        onClick={() => window.location.href = '#/matches'}
                        className="px-6 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors"
                    >
                        Browse Matches
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {profiles.map((profile, index) => {
                        const photoUrl = profile.photos?.[0];
                        const gender = profile.basicInfo?.gender;
                        const age = calculateAge(profile.basicInfo?.dob);

                        return (
                            <motion.div
                                key={profile._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                onClick={() => handleViewProfile(profile)}
                                className="bg-white rounded-lg border border-gray-200 hover:border-rose-200 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group"
                            >
                                {/* Profile Image */}
                                <div className="relative h-64 bg-gray-100 overflow-hidden">
                                    <img
                                        src={getProfileImage(photoUrl, gender)}
                                        alt={profile.basicInfo?.firstName || 'Profile'}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = gender === 'Female'
                                                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                : 'https://randomuser.me/api/portraits/men/85.jpg';
                                        }}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />

                                    {/* Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                                    {/* Time Ago Badge */}
                                    <div className="absolute top-3 left-3">
                                        <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1 shadow-sm">
                                            <FaClock className="text-rose-600" size={12} />
                                            <span className="text-xs font-bold">{formatTimeAgo(profile.viewedAt)}</span>
                                        </div>
                                    </div>

                                    {/* Verified Badge */}
                                    {profile.isVerified && (
                                        <div className="absolute top-3 right-3">
                                            <div className="bg-rose-500 text-white p-1.5 rounded-full">
                                                <FaUserCheck size={12} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Profile Info Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <h3 className="text-lg font-bold text-white mb-1">
                                            {profile.basicInfo?.firstName}, {age}
                                        </h3>
                                        <div className="flex items-center text-white/90 text-sm">
                                            <FaMapMarkerAlt size={12} className="mr-1" />
                                            <span>{profile.location?.currentCity || 'N/A'}</span>
                                        </div>
                                    </div>

                                    {/* Subtle diagonal center watermark - much more professional */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <span className="text-white text-2xl font-semibold tracking-widest opacity-30 rotate-[-30deg] select-none">
                                            ST Matchzone
                                        </span>
                                    </div>
                                </div>

                                {/* Profile Details */}
                                <div className="p-4">
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="flex items-center gap-2">
                                            <FaBriefcase className="text-gray-400" size={14} />
                                            <span className="text-sm text-gray-600 truncate">
                                                {profile.educationCareer?.occupation || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaGraduationCap className="text-gray-400" size={14} />
                                            <span className="text-sm text-gray-600 truncate">
                                                {profile.educationCareer?.education || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaPrayingHands className="text-gray-400" size={14} />
                                            <span className="text-sm text-gray-600 truncate">
                                                {profile.religionCommunity?.religion || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaVenusMars className="text-gray-400" size={14} />
                                            <span className="text-sm text-gray-600 truncate">
                                                {profile.basicInfo?.gender || 'N/A'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* View Count */}
                                    {profile.viewCount && profile.viewCount > 1 && (
                                        <div className="mb-4 flex items-center justify-center">
                                            <div className="bg-rose-50 text-rose-700 text-xs font-medium px-3 py-1 rounded-full">
                                                Viewed {profile.viewCount} times
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewProfile(profile);
                                        }}
                                        className="w-full bg-rose-600 text-white py-2.5 rounded-lg font-medium hover:bg-rose-700 transition-colors text-sm flex items-center justify-center gap-2"
                                    >
                                        <FaEye size={12} />
                                        View Again
                                    </button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ViewedProfiles;